import { Chess, swapColor } from "@chesshotel/chess";
import { exchangeEvaluation, getFen, uci2move } from "./util.js";
import { isValidFenMove } from "./tactics/util.js";
import { ChessTermType, TermAction, } from "@chesshotel/types";
export function detectMateThreatPositive(position, lastPosition) {
    const threat = detectMateThreat(lastPosition.fen, position.move);
    if (threat) {
        return {
            ...threat,
            termAction: TermAction.PERFORM,
            pvMoves: position.pvMoves,
        };
    }
}
export function detectMateThreatNegative(position, lastPosition) {
    const user = detectMateThreat(lastPosition.fen, position.move);
    const best = detectMateThreat(lastPosition.fen, lastPosition.bestMove);
    const arrows = best?.arrows ? [...best.arrows, best.move] : null;
    if (!user && best) {
        return {
            ...best,
            termAction: TermAction.MISS,
            arrows,
            pvMoves: lastPosition.pvMoves?.slice(0, 4),
        };
    }
    if (user && best) {
        return {
            ...best,
            termAction: TermAction.BETTER_WAY,
            arrows,
            pvMoves: lastPosition.pvMoves?.slice(0, 4),
        };
    }
    const opponentMove = position.pvMoves[0];
    const opponent = detectMateThreat(position.fen, opponentMove);
    if (opponent) {
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const isValid = isValidFenMove(bestFen, opponentMove);
        if (!isValid) {
            const arrows = [...opponent.arrows, opponent.move];
            return {
                ...opponent,
                termAction: TermAction.ALLOW,
                arrows,
            };
        }
        const bestOpponent = detectMateThreat(bestFen, opponentMove);
        if (!bestOpponent) {
            const arrows = [...opponent.arrows, opponent.move];
            return {
                ...opponent,
                termAction: TermAction.ALLOW,
                arrows,
            };
        }
    }
}
export function detectOpponentMateThreat(fen, moveStr) {
    const chess = new Chess(fen);
    const turnColor = chess.turn();
    const mateThreatBefore = detectMateThreatFen(fen, turnColor);
    chess.move(uci2move(moveStr));
    const mateThreatAfter = detectMateThreatFen(chess.fen(), turnColor);
    if (!mateThreatBefore && mateThreatAfter) {
        return true;
    }
    return false;
}
export function detectMateThreatFen(fen, color) {
    const chess = new Chess(fen);
    const turnColor = chess.turn();
    if (color == turnColor) {
        if (chess.inCheck()) {
            return false;
        }
        return chess.changeTurn();
    }
    const moves = chess.uciMoves({ legal: true, verbose: true });
    for (const move of moves) {
        chess.move(move);
        if (chess.isCheckmate()) {
            return true;
        }
        chess.undo();
    }
    return false;
}
const mateThreatCache = new Map();
const MATE_THREAT_CACHE_CAP = 5000;
export function detectMateThreat(fen, moveStr) {
    const cacheKey = `${fen}|${moveStr}`;
    if (mateThreatCache.has(cacheKey)) {
        return mateThreatCache.get(cacheKey);
    }
    const chess = new Chess(fen);
    const color = chess.turn();
    const opponentColor = swapColor(color);
    if (chess.inCheck()) {
        return null;
    }
    const move = chess.move(uci2move(moveStr));
    const oppAttackersAfterMove = chess.attackers(move.to, chess.turn());
    if (oppAttackersAfterMove.length > 0) {
        const evalResult = exchangeEvaluation(chess.fen(), move.to);
        if (evalResult.gain > 0 ||
            (evalResult.gain == 0 && evalResult.moveSequence.length > 1)) {
            return null;
        }
    }
    if (chess.isCheck()) {
        if (mateThreatCache.size > MATE_THREAT_CACHE_CAP)
            mateThreatCache.clear();
        mateThreatCache.set(cacheKey, null);
        return null;
    }
    chess.changeTurn();
    let isMateThreat = false;
    let mateMove = null;
    const candidates = chess.uciMoves({ legal: true });
    for (const candidate of candidates) {
        const moveResult = chess.move(uci2move(candidate));
        const oppAttackers = chess.attackers(moveResult.to, chess.turn());
        if (oppAttackers.length > 0) {
            const evalResult = exchangeEvaluation(chess.fen(), moveResult.to);
            if (evalResult.gain > 0 ||
                (evalResult.gain == 0 && evalResult.moveSequence.length > 1)) {
                chess.undo();
                continue;
            }
        }
        if (chess.isCheckmate()) {
            isMateThreat = true;
            mateMove = moveResult;
            break;
        }
        chess.undo();
    }
    if (isMateThreat) {
        const mainPiece = chess.getBoardPiece(move.to);
        const matePiece = chess.getBoardPiece(mateMove.to);
        const involvedPiece = move.to == mateMove.to ? matePiece : null;
        const result = {
            term: ChessTermType.THREATEN_CHECKMATE,
            mainPiece,
            involvedPiece,
            move,
            translationKeys: {
                color: opponentColor,
            },
            secondMove: mateMove,
            arrows: [mateMove],
        };
        if (mateThreatCache.size > MATE_THREAT_CACHE_CAP)
            mateThreatCache.clear();
        mateThreatCache.set(cacheKey, result);
        return result;
    }
    if (mateThreatCache.size > MATE_THREAT_CACHE_CAP)
        mateThreatCache.clear();
    mateThreatCache.set(cacheKey, null);
    return null;
}
//# sourceMappingURL=mateThreat.js.map